import { useState } from 'react'
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  ConfirmDialog, DetailTabs, EmptyState, ErrorState, FilterBar, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useCustomFields, useCustomFieldMutations, useCustomFieldSchema } from '../api'
import { CustomFieldForm } from '../components/CustomFieldInput'
import { CUSTOM_FIELD_TARGETS, CUSTOM_FIELD_TYPES } from '../types'
import type { CustomFieldDefinitionView, CustomFieldTarget, CustomFieldType } from '../types'
import { Select } from '@/components/ui/select'

type Draft = {
  id?: string
  code: string
  label: string
  helpText: string
  fieldType: CustomFieldType
  required: boolean
  optionsText: string
  regex: string
  minNumber: string
  maxNumber: string
  minLength: string
  maxLength: string
  sortOrder: string
  active: boolean
  searchable: boolean
  exportable: boolean
}

const EMPTY: Draft = {
  code: '', label: '', helpText: '', fieldType: 'TEXT', required: false, optionsText: '',
  regex: '', minNumber: '', maxNumber: '', minLength: '', maxLength: '',
  sortOrder: '0', active: true, searchable: false, exportable: true,
}

function toDraft(field: CustomFieldDefinitionView): Draft {
  return {
    id: field.id,
    code: field.code,
    label: field.label,
    helpText: field.helpText ?? '',
    fieldType: field.fieldType,
    required: field.required,
    optionsText: field.options.map((o) => `${o.value}=${o.label}`).join('\n'),
    regex: field.regex ?? '',
    minNumber: field.minNumber == null ? '' : String(field.minNumber),
    maxNumber: field.maxNumber == null ? '' : String(field.maxNumber),
    minLength: field.minLength == null ? '' : String(field.minLength),
    maxLength: field.maxLength == null ? '' : String(field.maxLength),
    sortOrder: String(field.sortOrder),
    active: field.active,
    searchable: field.searchable,
    exportable: field.exportable,
  }
}

function num(text: string): number | null {
  return text.trim() === '' ? null : Number(text)
}

/**
 * Admin-defined fields on the records that carry them.
 *
 * <p>The preview tab renders from `/custom-fields/schema` — the same endpoint every real form
 * uses. Previewing from a separate code path would let the designer and the finished form
 * disagree, which is exactly the bug a preview exists to prevent.
 */
export function CustomFieldsPage() {
  useBreadcrumb()
  const [target, setTarget] = useState<CustomFieldTarget>('EMPLOYEE')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [toWithdraw, setToWithdraw] = useState<CustomFieldDefinitionView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<Record<string, unknown>>({})

  const fields = useCustomFields(target)
  const schema = useCustomFieldSchema(target)
  const { create, update, withdraw } = useCustomFieldMutations()

  async function save() {
    if (!draft) return
    setError(null)
    const options = draft.optionsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        // "DAY=Day shift" — and "DAY" alone means the value is its own label.
        const [rawValue = '', ...rest] = line.split('=')
        const value = rawValue.trim()
        const label = rest.length > 0 ? rest.join('=').trim() : value
        return { value, label: label || value }
      })
      .filter((option) => option.value !== '')

    const body = {
      targetType: target,
      code: draft.code,
      label: draft.label,
      helpText: draft.helpText || null,
      fieldType: draft.fieldType,
      required: draft.required,
      options,
      regex: draft.regex || null,
      minNumber: num(draft.minNumber),
      maxNumber: num(draft.maxNumber),
      minLength: num(draft.minLength),
      maxLength: num(draft.maxLength),
      sortOrder: Number(draft.sortOrder || 0),
      active: draft.active,
      searchable: draft.searchable,
      exportable: draft.exportable,
    }

    try {
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, ...body })
      } else {
        await create.mutateAsync(body)
      }
      setDraft(null)
    } catch (caught) {
      // The server's message names the problem — an uncompilable pattern says where the
      // bracket is missing, which nothing generated here could.
      setError(toApiProblem(caught)?.detail ?? 'That field could not be saved')
    }
  }

  const list = (
    <Card>
      {fields.isPending && <Skeleton className="m-4 h-32" />}
      {fields.isError && <ErrorState error={fields.error} onRetry={() => void fields.refetch()} />}
      {fields.isSuccess && fields.data.length === 0 && (
        <EmptyState
          title="No custom fields yet"
          description="Add one and it appears on every record of this type, and in exports."
        />
      )}
      {(fields.data?.length ?? 0) > 0 && (
        <div className="overflow-x-auto overscroll-x-contain">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <th scope="col" className="p-3 font-medium">Label</th>
                <th scope="col" className="p-3 font-medium">Key</th>
                <th scope="col" className="p-3 font-medium">Type</th>
                <th scope="col" className="p-3 font-medium">Rules</th>
                <th scope="col" className="p-3 font-medium">Status</th>
                <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {fields.data?.map((field) => (
                <tr key={field.id} className="border-b border-[var(--color-border)] last:border-0">
                  <td className="p-3 font-medium text-[var(--color-heading)]">{field.label}</td>
                  <td className="p-3"><code className="text-xs">{field.code}</code></td>
                  <td className="p-3">{field.fieldType.toLowerCase()}</td>
                  <td className="p-3 text-xs text-[var(--color-muted)]">
                    {[
                      field.required && 'required',
                      field.regex && 'pattern',
                      field.minNumber != null || field.maxNumber != null ? 'range' : null,
                      field.minLength != null || field.maxLength != null ? 'length' : null,
                      field.searchable && 'searchable',
                    ].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="p-3">
                    <StatusBadge status={field.active ? 'ACTIVE' : 'INACTIVE'} />
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button" size="icon" variant="ghost"
                        aria-label={`Edit ${field.label}`}
                        onClick={() => { setError(null); setDraft(toDraft(field)) }}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button" size="icon" variant="ghost"
                        aria-label={`Withdraw ${field.label}`}
                        onClick={() => setToWithdraw(field)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )

  const previewPanel = (
    <Card className="space-y-4 p-5">
      <p className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
        <Eye className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          Rendered from the same <code>/custom-fields/schema</code> endpoint every real form
          uses, so what you see here is what people will actually get.
        </span>
      </p>
      {schema.isPending && <Skeleton className="h-32" />}
      {schema.isSuccess && schema.data.length === 0 && (
        <p className="text-sm text-[var(--color-muted)]">Nothing active to show yet.</p>
      )}
      <CustomFieldForm
        fields={schema.data ?? []}
        values={preview}
        onChange={(code, value) => setPreview((current) => ({ ...current, [code]: value }))}
      />
    </Card>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Custom fields"
        subtitle="Extra fields on your records, with validation, search and export."
        actions={
          <FilterBar>
            <Select
              aria-label="Fields on"
              id="cf-target"
              value={target}
              onChange={(event) => setTarget(event.target.value as CustomFieldTarget)}
            >
              {CUSTOM_FIELD_TARGETS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>

                        <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
                          <Plus className="size-4" aria-hidden="true" />
                          Add a field
                        </Button>
          </FilterBar>
        }
      />

      {CUSTOM_FIELD_TARGETS.find((t) => t.value === target)?.note && (
        <p className="rounded-[var(--radius-control)] bg-[var(--color-tint-blue)] px-3 py-2.5 text-sm text-[var(--color-body)]">
          Fields can be defined now — {CUSTOM_FIELD_TARGETS.find((t) => t.value === target)?.note}, so
          nothing renders them yet.
        </p>
      )}

      <DetailTabs
        tabs={[
          { key: 'fields', label: 'Fields', badge: fields.data?.length, content: list },
          { key: 'preview', label: 'Preview', content: previewPanel },
        ]}
      />

      <FormDialog
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
        title={draft?.id ? `Edit ${draft.label}` : 'Add a custom field'}
        description={
          draft?.id
            ? 'The key cannot change — it is how every value already stored is found.'
            : undefined
        }
        size="lg"
        loading={create.isPending || update.isPending}
        error={error}
        onSubmit={() => void save()}
      >
        {draft && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="cf-label" required>Label</Label>
                <Input
                  id="cf-label" required value={draft.label}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-code" required>Key</Label>
                <Input
                  id="cf-code" required value={draft.code} disabled={Boolean(draft.id)}
                  placeholder="shoe_size"
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                />
                <p className="text-xs text-[var(--color-muted)]">
                  Lower case, letters, digits and underscores. It becomes a JSON key, an export
                  header and a letter placeholder.
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cf-type" required>Type</Label>
              <Select
                id="cf-type" value={draft.fieldType}
                onChange={(e) => setDraft({ ...draft, fieldType: e.target.value as CustomFieldType })}
                className="w-full"
              >
                {CUSTOM_FIELD_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
              <p className="text-xs text-[var(--color-muted)]">
                {CUSTOM_FIELD_TYPES.find((t) => t.value === draft.fieldType)?.hint}
              </p>
            </div>

            {draft.fieldType === 'DROPDOWN' && (
              <div className="space-y-1.5">
                <Label htmlFor="cf-options" required>Choices</Label>
                <textarea
                  id="cf-options" rows={4} required value={draft.optionsText}
                  onChange={(e) => setDraft({ ...draft, optionsText: e.target.value })}
                  placeholder={'DAY=Day shift\nNIGHT=Night shift'}
                  className="w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 font-mono text-xs"
                />
                <p className="text-xs text-[var(--color-muted)]">
                  One per line, as <code>value=label</code>. The value is what gets stored.
                </p>
              </div>
            )}

            {draft.fieldType === 'TEXT' && (
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cf-min-len">Min length</Label>
                  <Input id="cf-min-len" type="number" min={0} value={draft.minLength}
                         onChange={(e) => setDraft({ ...draft, minLength: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf-max-len">Max length</Label>
                  <Input id="cf-max-len" type="number" min={0} value={draft.maxLength}
                         onChange={(e) => setDraft({ ...draft, maxLength: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf-regex">Pattern</Label>
                  <Input id="cf-regex" value={draft.regex} placeholder="^[A-Z]{2}-\d{4}$"
                         className="font-mono text-xs"
                         onChange={(e) => setDraft({ ...draft, regex: e.target.value })} />
                </div>
              </div>
            )}

            {draft.fieldType === 'NUMBER' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cf-min">Minimum</Label>
                  <Input id="cf-min" type="number" value={draft.minNumber}
                         onChange={(e) => setDraft({ ...draft, minNumber: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cf-max">Maximum</Label>
                  <Input id="cf-max" type="number" value={draft.maxNumber}
                         onChange={(e) => setDraft({ ...draft, maxNumber: e.target.value })} />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="cf-help">Help text</Label>
              <Input id="cf-help" value={draft.helpText}
                     onChange={(e) => setDraft({ ...draft, helpText: e.target.value })} />
            </div>

            <fieldset className="flex flex-wrap gap-4">
              <legend className="sr-only">Options</legend>
              {([
                ['required', 'Required'],
                ['active', 'Active'],
                ['searchable', 'Searchable'],
                ['exportable', 'Include in exports'],
              ] as const).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft[key]}
                    onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
          </>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toWithdraw !== null}
        onOpenChange={(open) => !open && setToWithdraw(null)}
        title="Withdraw this field?"
        description={
          toWithdraw
            ? `${toWithdraw.label} stops appearing on forms. Values already saved are kept, so nothing is lost.`
            : undefined
        }
        confirmLabel="Withdraw"
        destructive
        loading={withdraw.isPending}
        onConfirm={() => {
          if (toWithdraw) {
            withdraw.mutate(toWithdraw.id, { onSettled: () => setToWithdraw(null) })
          }
        }}
      />
    </div>
  )
}
